/**
 * RedactionQueriesCollapsible — queries de recherche groupées par argument juridique,
 * avec suivi de progression par cercle SVG.
 *
 * Port web de `apps/addin-word/src/taskpane/components/hector-ai/QueriesCollapsible.tsx`.
 * Différences vs add-in :
 * - Palette stone-* (vs gray-*)
 * - stroke="#1c1917" (stone-900) au lieu de stroke="#262626" (charcoal) dans CircularProgress
 * - track SVG : "#e7e5e4" (stone-200) au lieu de "#f3f4f6" (gray-100)
 */

import * as React from "react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SearchQuery, ResearchPhase } from "@/features/redaction/types/research-progress";

/**
 * Groupe d'avancement par argument juridique.
 *
 * En V1-bis, 1 argument = 1 unité de travail côté UI (le KeywordGenerator
 * produit ~5 queries Judilibre internes, mais c'est un détail invisible).
 * On garde un compteur `queries.length` interne pour la moyenne de
 * progression mais on n'affiche plus "X/Y recherches" — qui héritait du
 * modèle V0 biptyque (Conditions/Preuves + Effets/Sanctions = 2 queries
 * par argument) abandonné depuis 2026-05-03.
 */
interface ArgumentGroup {
  argumentTitle: string;
  argumentId: string;
  queries: SearchQuery[];
  completedCount: number;
  totalCount: number;
}

interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/** Cercle de progression monochrome (stone-900 sur piste stone-200). */
function CircularProgress({ percentage, size = 24, strokeWidth = 2.5 }: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <div
      className="relative flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Piste : stone-200 (#e7e5e4) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e7e5e4"
          strokeWidth={strokeWidth}
        />
        {/* Arc de progression : stone-900 (#1c1917) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1c1917"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-300"
        />
      </svg>
    </div>
  );
}

export interface RedactionQueriesCollapsibleProps {
  queries: SearchQuery[];
  completedCount: number;
  totalCount: number;
  currentPhase: ResearchPhase;
}

export function RedactionQueriesCollapsible({
  queries,
  completedCount,
  totalCount,
  // currentPhase est fourni par le parent pour usage futur (phase-gating des groupes)
  // mais n'est pas consommé dans ce composant presentationnel.
  currentPhase: _currentPhase,
}: RedactionQueriesCollapsibleProps) {
  const argumentGroups = React.useMemo(() => {
    const groups = new Map<string, ArgumentGroup>();
    queries.forEach((query) => {
      const key = query.argumentTitle;
      if (!groups.has(key)) {
        groups.set(key, {
          argumentTitle: query.argumentTitle,
          argumentId: query.argumentId,
          queries: [],
          completedCount: 0,
          totalCount: 0,
        });
      }
      const group = groups.get(key)!;
      group.queries.push(query);
      group.totalCount++;
      if (query.status === "complete") group.completedCount++;
    });
    return Array.from(groups.values());
  }, [queries]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-stone-700">Recherche de JP</span>
        <span className="text-stone-400 tabular-nums">
          {completedCount}/{totalCount}
        </span>
      </div>

      <div className="space-y-1.5">
        {argumentGroups
          .filter((group) => group.queries.some((q) => q.status !== "pending"))
          .map((group) => (
            <ArgumentCollapsibleItem key={group.argumentId} group={group} />
          ))}
      </div>
    </div>
  );
}

function ArgumentCollapsibleItem({ group }: { group: ArgumentGroup }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [queryAutoProgress, setQueryAutoProgress] = React.useState<Record<number, number>>({});
  const allComplete = group.completedCount === group.totalCount;

  // Auto-incrémente la progression des queries "processing" sans batchProgress
  // à 1 Hz, plafonné à 50 % pour éviter de fausser l'UI avant la réponse réelle.
  React.useEffect(() => {
    const processingQueries = group.queries.filter(
      (q) => q.status === "processing" && !q.batchProgress
    );
    if (processingQueries.length > 0) {
      const interval = setInterval(() => {
        setQueryAutoProgress((prev) => {
          const updated = { ...prev };
          processingQueries.forEach((q) => {
            const current = updated[q.index] ?? 0;
            if (current < 50) updated[q.index] = current + 2;
          });
          return updated;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [group.queries]);

  const argumentProgress = React.useMemo(() => {
    if (allComplete) return 100;
    if (group.totalCount === 0) return 0;
    let total = 0;
    group.queries.forEach((query) => {
      if (query.status === "complete") {
        total += 100;
      } else if (query.status === "processing") {
        total += query.batchProgress
          ? query.batchProgress.percentComplete
          : (queryAutoProgress[query.index] ?? 0);
      }
    });
    return total / group.totalCount;
  }, [group.queries, allComplete, group.totalCount, queryAutoProgress]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger
        className={cn(
          "w-full flex items-center gap-2 p-2 rounded-md border border-stone-100 hover:bg-stone-50 transition-colors cursor-pointer"
        )}
      >
        <ChevronRight
          className={cn(
            "w-3 h-3 text-stone-400 transition-transform shrink-0",
            isOpen && "rotate-90"
          )}
        />
        <CircularProgress percentage={argumentProgress} size={20} strokeWidth={1.5} />
        <div className="flex-1 text-left min-w-0 pr-1">
          <div className="text-xs text-stone-700 line-clamp-2 break-words leading-tight">
            {group.argumentTitle}
          </div>
          <div className="text-xs text-stone-400 mt-0.5">
            {allComplete ? "Recherche terminée" : "Recherche en cours…"}
          </div>
        </div>
        {allComplete && (
          <span className="text-xs text-stone-500 tabular-nums shrink-0">
            {group.queries.reduce((sum, q) => sum + (q.decisionsRelevant ?? 0), 0)} JP
          </span>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="ml-6 mt-1 space-y-1">
          {group.queries
            .filter((query) => query.status !== "pending")
            .map((query) => (
              <QueryItem key={query.index} query={query} />
            ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function QueryItem({ query }: { query: SearchQuery }) {
  const [autoProgress, setAutoProgress] = React.useState(0);

  // Même logique d'auto-progression 1 Hz / cap 50 % que ArgumentCollapsibleItem,
  // mais au niveau de la query individuelle pour le cercle de détail.
  React.useEffect(() => {
    if (query.status === "processing" && !query.batchProgress) {
      const interval = setInterval(() => {
        setAutoProgress((prev) => (prev < 50 ? prev + 2 : prev));
      }, 1000);
      return () => clearInterval(interval);
    }
    setAutoProgress(0);
    return undefined;
  }, [query.status, query.batchProgress]);

  const queryProgress = React.useMemo(() => {
    if (query.status === "complete") return 100;
    if (query.status !== "processing") return 0;
    return query.batchProgress ? query.batchProgress.percentComplete : autoProgress;
  }, [query.status, query.batchProgress, autoProgress]);

  // En V1-bis le champ `angle` legacy ("Conditions/Preuves" / "Effets/Sanctions")
  // n'est plus émis. On affiche juste le décompte JP retenues / extraites.
  const displayText = React.useMemo(() => {
    if (
      query.status === "complete" &&
      query.decisionsRelevant !== undefined &&
      query.decisionsFound !== undefined
    ) {
      return `${query.decisionsRelevant} JP retenues sur ${query.decisionsFound} analysées`;
    }
    if (query.status === "processing") return "Analyse en cours…";
    return "";
  }, [query.status, query.decisionsRelevant, query.decisionsFound]);

  return (
    <div className="flex items-center gap-2 p-1.5 rounded border border-stone-100">
      <CircularProgress percentage={queryProgress} size={16} strokeWidth={1.5} />
      <span className="text-xs text-stone-600">{displayText}</span>
    </div>
  );
}
